import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useGuidedTour } from "../GuidedTourProvider";
import { getTour, TOUR_ORDER, TOURS } from "../tours";
import { t } from "../i18n";
import { playStep, stopStep, setMuted } from "../audio";
import SpotlightOverlay from "./SpotlightOverlay";
import AvatarWidget from "./AvatarWidget";

/**
 * Orchestrates a running tour on desktop. v2 upgrades:
 *
 *   • Active remeasure via ResizeObserver + MutationObserver — the
 *     spotlight follows the target if the page re-renders or the
 *     element moves.
 *   • If the target DOM node vanishes for >500ms, drop into plain-dim
 *     mode (widget stays visible) so a broken tour never leaves the
 *     user stuck on a frozen overlay.
 *   • If the user navigates to a path matching another step in the
 *     current tour, fast-forward to that step. Covers the "I already
 *     know this — let me just get on with it" flow.
 *   • Each step triggers the matching Hebrew MP3.
 */
const MISSING_GRACE_MS = 500;

const TourPlayer = () => {
  const {
    activeTour, stepIndex, nextStep, prevStep, endTour, startTour,
    setTourStepProgress, completeTourProgress,
    muted,
  } = useGuidedTour();
  const navigate = useNavigate();
  const location = useLocation();

  const [targetRect, setTargetRect] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const observerRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const missingTimerRef = useRef(null);
  const lastMeasuredNodeRef = useRef(null);
  // Set to true whenever the player itself fires navigate() for a
  // step transition. The user-initiated fast-forward effect checks
  // and clears this flag so a programmatic navigation isn't mistaken
  // for the user manually wandering to a different step's path.
  const justNavigatedRef = useRef(false);
  // Tracks the last step we performed the auto-navigate for. We only
  // auto-navigate to a step's path on the FIRST render of that step,
  // not on every subsequent location.pathname change. Otherwise the
  // observer effect yanks the user back when they click any sidebar
  // item, because the effect's deps include location.pathname.
  const lastNavigatedStepKeyRef = useRef("");

  const tour = activeTour ? getTour(activeTour) : null;
  const step = tour?.steps?.[stepIndex] || null;
  const isLast = tour && stepIndex >= tour.steps.length - 1;

  // Tour step paths can contain `:me` — resolve it to the logged-in
  // user's id from localStorage so e.g. /working-hours/:me works.
  // Resolves to null if there's no user_id yet (we won't navigate
  // there in that case — auth gate below catches it).
  const resolveStepPath = (raw) => {
    if (!raw || typeof raw !== "string") return raw;
    if (!raw.includes(":me")) return raw;
    let userId = "";
    try { userId = JSON.parse(localStorage.getItem("user_id") || '""'); } catch (_) {}
    if (!userId) return null;
    return raw.replace(":me", userId);
  };
  const stepPath = resolveStepPath(step?.path);

  // Play audio per step (respects mute toggle).
  useEffect(() => {
    if (!tour || !step) return;
    if (muted) {
      setMuted(true);
      return;
    }
    setMuted(false);
    playStep(step.id);
    return () => stopStep();
  }, [tour, step, muted]);

  // Measurement logic — called whenever we suspect the target moved.
  const measure = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      setTargetMissing(false);
      return;
    }
    const node = document.querySelector(`[data-tour="${step.target}"]`);
    if (!node) {
      // Schedule the missing-grace timer — if still missing after 500ms,
      // switch to plain-dim mode.
      clearTimeout(missingTimerRef.current);
      missingTimerRef.current = setTimeout(() => {
        setTargetMissing(true);
        setTargetRect(null);
      }, MISSING_GRACE_MS);
      return;
    }
    clearTimeout(missingTimerRef.current);
    setTargetMissing(false);
    lastMeasuredNodeRef.current = node;
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    setTargetRect({
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }, [step]);

  // Set up observers for the current step.
  useEffect(() => {
    if (!tour || !step) return;

    // Auth gate: refuse to drive the user into authed routes when they
    // aren't logged in. Without this, clicking a tour from the avatar
    // on /auth/login navigates straight into /management even though
    // there's no token, leaving the dashboard unable to load data.
    // Tour gets ended cleanly so the next login session starts fresh.
    let hasToken = false;
    try { hasToken = !!localStorage.getItem("token"); } catch (_) {}
    if (step.path && !hasToken) {
      stopStep();
      endTour();
      if (!location.pathname.startsWith("/auth")) {
        navigate("/auth/login");
      }
      return;
    }

    // Auto-navigate only on the first render of a step. After that,
    // the user is free to click around — the user-initiated effect
    // below either fast-forwards (if the new path matches a later
    // step) or ends the tour (if they wander off entirely).
    const stepKey = `${tour.id}#${stepIndex}`;
    if (lastNavigatedStepKeyRef.current !== stepKey) {
      lastNavigatedStepKeyRef.current = stepKey;
      setTourStepProgress(tour.id, stepIndex);
      if (stepPath && location.pathname !== stepPath) {
        justNavigatedRef.current = true;
        navigate(stepPath);
      }
    }

    // Initial measure + a retry burst (first ~600ms) — covers route
    // transitions and lazy-loaded screens.
    let retryCount = 0;
    const retry = () => {
      measure();
      if (retryCount++ < 5) setTimeout(retry, 120);
    };
    const firstTimer = setTimeout(retry, 80);

    // Active observers: any DOM mutation in the tree triggers a re-measure.
    mutationObserverRef.current?.disconnect();
    mutationObserverRef.current = new MutationObserver(() => measure());
    mutationObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    // ResizeObserver: resizes on the target itself.
    observerRef.current?.disconnect();
    if (typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(() => measure());
      // Attach after a tick so measure() has had a chance to find the node.
      setTimeout(() => {
        if (lastMeasuredNodeRef.current) {
          observerRef.current?.observe(lastMeasuredNodeRef.current);
        }
      }, 500);
    }

    const handleResize = () => measure();
    const handleScroll = () => measure();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      clearTimeout(firstTimer);
      clearTimeout(missingTimerRef.current);
      mutationObserverRef.current?.disconnect();
      observerRef.current?.disconnect();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [tour, step, stepIndex, location.pathname, navigate, measure, setTourStepProgress, endTour]);

  // User-initiated path change: fast-forward to a step that matches.
  // Apply the same `:me` resolution before comparing.
  useEffect(() => {
    if (!tour) return;
    // Skip if the player itself just fired the navigate() call —
    // otherwise consecutive steps that share a path (e.g. step 3 and
    // step 4 both on /create-appointment) end up auto-skipping each
    // other because findIndex always picks the higher-indexed match.
    if (justNavigatedRef.current) {
      justNavigatedRef.current = false;
      return;
    }
    // 1. Did the user land on a future step's path? Fast-forward.
    const matching = tour.steps.findIndex(
      (s, i) => i > stepIndex && resolveStepPath(s.path) === location.pathname && s.target
    );
    if (matching !== -1) {
      // startTour restarts the tour internally at the new index, which
      // updates both the live stepIndex and the persisted progress.
      startTour(tour.id, matching);
      return;
    }
    // 2. Still on the current step's path? Stay put.
    if (stepPath === location.pathname) return;
    // 3. User navigated outside the tour entirely — they explicitly
    //    chose to leave. End the tour cleanly instead of dragging
    //    them back. This was the source of "sidebar clicks don't
    //    navigate" — observer effect kept yanking the user back to
    //    step.path on every location change.
    stopStep();
    endTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNext = useCallback(() => {
    if (!tour) return;
    if (isLast) {
      stopStep();
      completeTourProgress(tour.id);
      endTour();
    } else {
      nextStep();
    }
  }, [tour, isLast, completeTourProgress, endTour, nextStep]);

  const handleClose = useCallback(() => {
    stopStep();
    endTour();
  }, [endTour]);

  if (!tour || !step) return null;

  const body = t(step.i18nBody);
  const hint = step.hint ? t(step.hint) : null;

  return (
    <>
      <SpotlightOverlay rect={targetRect} onClick={handleNext} />
      <AvatarWidget
        body={body}
        hint={targetMissing ? hint || t("tours.player.skip") : hint}
        stepIndex={stepIndex}
        totalSteps={tour.steps.length}
        onPrev={prevStep}
        onNext={handleNext}
        onClose={handleClose}
        canGoPrev={stepIndex > 0}
        canGoNext
      />
    </>
  );
};

export default TourPlayer;
